package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.auth.LocalAdminAuth
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.UserDetailContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.api.enums.AdminRole
import com.merkost.handpass.api.dto.admin.AdminUserDetail
import com.merkost.handpass.api.dto.admin.BanPreviewResponse
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.merkost.handpass.admin.di.koinContainer
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/users/{id}")
@Composable
fun UserDetailPage() {
    val ctx = rememberPageContext()
    val userId = ctx.route.params["id"] ?: ""
    AdminPageScaffold(title = "User Detail", currentPath = "/users", showTitle = false) {
        val container = koinContainer<UserDetailContainer>()
        var pendingConfirm by remember { mutableStateOf<Pair<UserDetailActionKind, ConfirmConfig>?>(null) }
        var pendingDeleteNoteId by remember { mutableStateOf<String?>(null) }

        LaunchedEffect(userId) { container.store.intent(UserDetailIntent.Init(userId)) }

        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is UserDetailAction.Navigate -> navigate(action.path)
                    is UserDetailAction.ShowSuccess -> toast(action.message, ToastKind.Success)
                    is UserDetailAction.ShowUndoableSuccess -> showUndoToast(
                        message = action.message,
                        onUndo = { intent(UserDetailIntent.UndoRestore(action.userId)) },
                    )
                    is UserDetailAction.ShowError -> toast(action.message, ToastKind.Error)
                    is UserDetailAction.OpenReasonModal -> pendingConfirm = action.kind to confirmConfigFor(action.kind, action.targetName, action.banPreview)
                }
            }
            when (val s = state) {
                UserDetailState.Loading -> AdminCard { AdminSkeletonTable(rows = 4) }
                is UserDetailState.Error -> AdminEmptyState(
                    title = "Failed to load",
                    description = s.message,
                    actionLabel = "Retry",
                    onAction = { intent(UserDetailIntent.Refresh) },
                )
                is UserDetailState.Content -> UserDetailContent(
                    s = s,
                    onIntent = ::intent,
                    onBack = { navigate("/users") },
                    onRequestDeleteNote = { pendingDeleteNoteId = it },
                )
            }

            pendingConfirm?.let { (kind, config) ->
                AdminConfirmDialog(
                    config = config,
                    onConfirm = { reason ->
                        intent(UserDetailIntent.ConfirmAction(kind, reason))
                        pendingConfirm = null
                    },
                    onDismiss = { pendingConfirm = null },
                )
            }

            pendingDeleteNoteId?.let { noteId ->
                AdminConfirmDialog(
                    config = ConfirmConfig(
                        title = "Delete note",
                        message = "Delete this admin note? This can't be undone.",
                        confirmLabel = "Delete",
                        isDestructive = true,
                    ),
                    onConfirm = {
                        intent(UserDetailIntent.DeleteNote(noteId))
                        pendingDeleteNoteId = null
                    },
                    onDismiss = { pendingDeleteNoteId = null },
                )
            }
        }
    }
}

private fun confirmConfigFor(kind: UserDetailActionKind, name: String, banPreview: BanPreviewResponse? = null): ConfirmConfig = when (kind) {
    UserDetailActionKind.Warn -> ConfirmConfig(
        title = "Warn user",
        message = "Issue a warning to $name?",
        confirmLabel = "Warn",
        reasonRequired = true,
    )
    UserDetailActionKind.Suspend -> ConfirmConfig(
        title = "Suspend user",
        message = "Suspend $name?",
        confirmLabel = "Suspend",
        isDestructive = true,
        reasonRequired = true,
    )
    UserDetailActionKind.Ban -> ConfirmConfig(
        title = "Ban user",
        message = "Permanently ban $name?",
        confirmLabel = "Ban",
        isDestructive = true,
        typeToConfirm = name,
        callout = banImpactCallout(name, banPreview),
        reasonRequired = true,
    )
    UserDetailActionKind.Restore -> ConfirmConfig(
        title = "Restore user",
        message = "Restore $name's account?",
        confirmLabel = "Restore",
    )
}

private fun banImpactCallout(name: String, preview: BanPreviewResponse?): String {
    if (preview == null) return "This will revoke auth, hide active listings, and withdraw pending offers."
    fun plural(count: Long, singular: String) = "$count $singular" + if (count == 1L) "" else "s"
    return "Banning $name will hide ${plural(preview.activeListings, "active listing")}, " +
        "withdraw ${plural(preview.pendingOffers, "pending offer")}, and affect " +
        "${plural(preview.conversations, "conversation")}. Auth access is revoked immediately."
}

@Composable
private fun AdminPageScope.UserDetailContent(
    s: UserDetailState.Content,
    onIntent: (UserDetailIntent) -> Unit,
    onBack: () -> Unit,
    onRequestDeleteNote: (String) -> Unit,
) {
    val profile = s.detail.profile

    AdminPageHeader(
        title = profile.fullName,
        subtitle = profile.email,
        actions = {
            AdminButton(label = "← Users", onClick = onBack, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
        },
    )

    AdminCard(paddingScale = AdminCardPadding.Spacious) {
        Div({ style { display(DisplayStyle.Flex); gap(HandpassSpacing.XL); alignItems(AlignItems.FlexStart) } }) {
            AdminAvatar(name = profile.fullName, url = profile.avatarUrl, size = AdminAvatarSize.LG)
            Div({ style { property("flex", "1") } }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(HandpassSpacing.MD)
                        property("margin-bottom", "${HandpassSpacing.SM}")
                    }
                }) {
                    H2({
                        style {
                            fontSize(HandpassTypography.Title)
                            fontWeight(700)
                            property("margin", "0")
                            color(OnSurface.value())
                        }
                    }) { Text(profile.fullName) }
                    AccountStatusBadge(profile.accountStatus.value)
                }
                Div({
                    style {
                        fontSize(HandpassTypography.Body)
                        color(OnSurfaceMuted.value())
                        property("margin-bottom", "${HandpassSpacing.XS}")
                    }
                }) { Text(profile.email) }
                profile.phoneNumber?.let {
                    Div({ style { fontSize(HandpassTypography.Body); color(OnSurfaceMuted.value()) } }) { Text(it) }
                }
            }
            UserDetailActionColumn(suspendDays = s.suspendDays, onIntent = onIntent)
        }
    }

    AdminTabs(
        tabs = listOf(
            AdminTab(UserDetailTab.Listings, "Listings", s.detail.listings.size.toLong()),
            AdminTab(UserDetailTab.Reports, "Reports", s.detail.reportsAgainst.size.toLong()),
            AdminTab(UserDetailTab.History, "Moderation History", s.detail.moderationHistory.size.toLong()),
            AdminTab(UserDetailTab.Notes, "Notes"),
            AdminTab(UserDetailTab.Conversations, "Conversations"),
            AdminTab(UserDetailTab.ViewedBy, "Viewed by"),
        ),
        selected = s.activeTab,
        onSelect = { onIntent(UserDetailIntent.SetTab(it)) },
    )

    when (s.activeTab) {
        UserDetailTab.Listings -> UserDetailListingsTab(detail = s.detail)
        UserDetailTab.Reports -> UserDetailReportsTab(detail = s.detail)
        UserDetailTab.History -> UserDetailHistoryTab(detail = s.detail)
        UserDetailTab.Notes -> AdminNotesSection(
            draft = s.newNoteDraft,
            notes = s.notes,
            onDraftChange = { onIntent(UserDetailIntent.SetNoteDraft(it)) },
            onSubmit = { onIntent(UserDetailIntent.SubmitNote) },
            onRequestDelete = onRequestDeleteNote,
            onRetry = { onIntent(UserDetailIntent.Refresh) },
        )
        UserDetailTab.Conversations -> AdminConversationsView(
            conversations = s.conversations,
            openConversation = s.openConversation,
            messages = s.messages,
            onOpen = { onIntent(UserDetailIntent.OpenConversation(it)) },
            onClose = { onIntent(UserDetailIntent.CloseConversation) },
            onRetryConversations = { onIntent(UserDetailIntent.LoadConversations) },
            onRetryMessages = { s.openConversation?.let { onIntent(UserDetailIntent.OpenConversation(it)) } },
        )
        UserDetailTab.ViewedBy -> AdminViewedBySection(
            audit = s.audit,
            onRetry = { onIntent(UserDetailIntent.Refresh) },
        )
    }
}

@Composable
private fun UserDetailActionColumn(suspendDays: Int, onIntent: (UserDetailIntent) -> Unit) {
    val canActOnUsers = LocalAdminAuth.current.role.isAtLeast(AdminRole.ADMIN)
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.SM) } }) {
        if (canActOnUsers) {
            Div({ style { display(DisplayStyle.Flex); gap(HandpassSpacing.XS) } }) {
                listOf(1, 7, 30).forEach { days ->
                    AdminButton(
                        label = "${days}d",
                        onClick = { onIntent(UserDetailIntent.SetSuspendDays(days)) },
                        variant = if (suspendDays == days) AdminButtonVariant.Primary else AdminButtonVariant.Secondary,
                        size = AdminButtonSize.SM,
                    )
                }
            }
        }
        AdminButton(label = "Warn", onClick = { onIntent(UserDetailIntent.TapWarn) }, variant = AdminButtonVariant.Secondary, size = AdminButtonSize.SM)
        if (canActOnUsers) {
            AdminButton(label = "Suspend", onClick = { onIntent(UserDetailIntent.TapSuspend) }, variant = AdminButtonVariant.Secondary, size = AdminButtonSize.SM)
            AdminButton(label = "Ban", onClick = { onIntent(UserDetailIntent.TapBan) }, variant = AdminButtonVariant.Destructive, size = AdminButtonSize.SM)
            AdminButton(label = "Restore", onClick = { onIntent(UserDetailIntent.TapRestore) }, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
        }
    }
}

@Composable
private fun UserDetailListingsTab(detail: AdminUserDetail) {
    if (detail.listings.isEmpty()) {
        AdminEmptyState(title = "No listings")
        return
    }
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
            gap(HandpassSpacing.Default)
        }
    }) {
        detail.listings.forEach { listing ->
            AdminCard {
                Div({
                    style {
                        fontSize(HandpassTypography.Body)
                        fontWeight(500)
                        color(OnSurface.value())
                        property("margin-bottom", "${HandpassSpacing.XS}")
                    }
                }) { Text(listing.title) }
                AccountStatusBadge(listing.status)
            }
        }
    }
}

@Composable
private fun UserDetailReportsTab(detail: AdminUserDetail) {
    if (detail.reportsAgainst.isEmpty()) {
        AdminEmptyState(title = "No reports")
        return
    }
    detail.reportsAgainst.forEach { r ->
        AdminCard {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    property("margin-bottom", "${HandpassSpacing.XS}")
                }
            }) {
                Span({ style { fontWeight(500); fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) { Text(r.reason) }
                Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()) } }) { Text(r.createdAt?.take(10) ?: "") }
            }
            r.details?.let { Div({ style { fontSize(HandpassTypography.Body); color(OnSurfaceMuted.value()) } }) { Text(it) } }
            Div({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()); property("margin-top", "${HandpassSpacing.XS}") } }) {
                Text("By: ${r.reporterName ?: r.reporterId.take(8)}")
            }
        }
    }
}

@Composable
private fun UserDetailHistoryTab(detail: AdminUserDetail) {
    if (detail.moderationHistory.isEmpty()) {
        AdminEmptyState(title = "No moderation history")
        return
    }
    detail.moderationHistory.forEach { log ->
        AdminCard {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    property("margin-bottom", "${HandpassSpacing.XS}")
                }
            }) {
                Span({ style { fontWeight(500); fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) { Text(log.action.replace("_", " ")) }
                Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()) } }) { Text(log.createdAt.take(10)) }
            }
            log.reason?.let { Div({ style { fontSize(HandpassTypography.Body); color(OnSurfaceMuted.value()) } }) { Text(it) } }
            Div({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()); property("margin-top", "${HandpassSpacing.XS}") } }) {
                Text("Admin: ${log.adminName ?: "Unknown"}")
            }
        }
    }
}

